/**
 * Boot-splash branding — the mark and colours drawn INSIDE index.html's
 * pre-resolve `#boot-splash` on a return visit.
 *
 * WHY. The splash is brand-less by construction: the tenant is not known
 * until `GET /tenant-apps/resolve` answers, and a white-label tab must never
 * flash the Metaship mark. But the splash also stays on screen for the WHOLE
 * resolve (main.ts removes it only after the app mounts), so the initial
 * loading animation never showed an icon at all — the branded OrbitalLoader
 * only takes over afterwards (reported for crm.metaship.ai, 2026-09-02).
 *
 * HOW. After every successful resolve the shell remembers, per hostname, the
 * few values the splash needs (name, one mark URL, primary/secondary). On the
 * next boot `paintBootSplash()` reads that record synchronously — before any
 * network wait — and draws it. A first visit stays neutral, which is exactly
 * the white-label rule: the splash only ever shows what THIS host has itself
 * resolved before. A `found:false` resolve forgets the record, so a disabled
 * or deleted app stops showing its old brand on the next load.
 *
 * The record is keyed on `location.hostname`, and only written when that is
 * the host that was resolved — a dev `?tenantHost=` override on localhost
 * never leaves one tenant's brand behind for the next.
 */
import type { ResolveTenantAppResponse } from "./types";

export const SPLASH_KEY_PREFIX = "metaship:splash:";

export interface SplashRecord {
  name: string;
  /** The mark drawn RAW on the splash's dark ground — the dark-chrome variant first. */
  markUrl: string | null;
  primary: string | null;
  secondary: string | null;
}

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

function splashKey(hostname: string): string {
  return `${SPLASH_KEY_PREFIX}${hostname.toLowerCase()}`;
}

/** Only an https URL is ever painted — the record lives in localStorage, so treat it as untrusted on the way back in. */
function safeUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    return new URL(value).protocol === "https:" ? value : null;
  } catch {
    return null;
  }
}

function safeColour(value: unknown): string | null {
  return typeof value === "string" && HEX_COLOUR.test(value) ? value : null;
}

/**
 * The splash record for a resolved tenant, or null when there is nothing safe
 * to show (not found, or no branding). The splash ground is ALWAYS dark
 * (index.html), so the mark follows OrbitalLoader's dark-theme rule:
 * `logoDarkUrl`, falling back to `logoLightUrl` — one variant beats an empty
 * centre.
 */
export function toSplashRecord(tenant: ResolveTenantAppResponse): SplashRecord | null {
  if (!tenant.found || !tenant.branding) return null;
  const { branding } = tenant;
  return {
    name: tenant.name || "",
    markUrl: safeUrl(branding.logoDarkUrl) ?? safeUrl(branding.logoLightUrl),
    primary: safeColour(branding.colours?.primary),
    secondary: safeColour(branding.colours?.secondary),
  };
}

export function readSplash(hostname: string, storage: Storage = localStorage): SplashRecord | null {
  try {
    const raw = storage.getItem(splashKey(hostname));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SplashRecord> | null;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      name: typeof parsed.name === "string" ? parsed.name : "",
      markUrl: safeUrl(parsed.markUrl),
      primary: safeColour(parsed.primary),
      secondary: safeColour(parsed.secondary),
    };
  } catch {
    return null;
  }
}

/**
 * Remembers (or forgets, on `found:false`) the splash record for `hostname`.
 * `resolvedHost` is the host the shell actually resolved — when it differs
 * from `hostname` (a dev override), nothing is written.
 */
export function rememberSplash(
  hostname: string,
  resolvedHost: string,
  tenant: ResolveTenantAppResponse,
  storage: Storage = localStorage,
): void {
  if (hostname.toLowerCase() !== resolvedHost.toLowerCase()) return;
  try {
    const record = toSplashRecord(tenant);
    if (record) storage.setItem(splashKey(hostname), JSON.stringify(record));
    else storage.removeItem(splashKey(hostname));
  } catch {
    // best-effort — the next boot just stays neutral
  }
}

/**
 * Draws the remembered brand into index.html's `#boot-splash`: the mark in
 * the centre, the tenant colours on the orbits (via the `--boot-primary` /
 * `--boot-secondary` custom properties the splash CSS reads), and the tab
 * title. A no-op without a record or without the splash element (already
 * removed, or a test DOM).
 */
export function paintBootSplash(record: SplashRecord | null, doc: Document = document): void {
  if (!record) return;
  const splash = doc.getElementById("boot-splash");
  if (!splash) return;

  if (record.primary) splash.style.setProperty("--boot-primary", record.primary);
  if (record.secondary) splash.style.setProperty("--boot-secondary", record.secondary);

  const slot = splash.querySelector<HTMLElement>(".orbit-mark");
  if (record.markUrl && slot && !slot.querySelector("img")) {
    const img = doc.createElement("img");
    img.src = record.markUrl;
    img.alt = "";
    img.setAttribute("referrerpolicy", "no-referrer");
    img.decoding = "async";
    slot.appendChild(img);
  }

  if (record.name) doc.title = record.name;
}
